import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import Decimal from 'decimal.js';
import {
  Form,
  FormSection,
  FormField,
  FormActions,
  TextField,
  useCgForm,
} from 'modules/Common/form/v2';
import IndirectEmissionsFields from './IndirectEmissionsFields';
import { getCbamFactorForYear } from 'modules/Common/utils/Utility';
import { supplierFactorDraftProposedFactorV1Schema } from 'modules/FreeSupplier/supplierDraftContracts';
import {
  currentlyPublishedDisplayValue,
  findMatchingSupplierFactor,
  formatDraftLastRecordDate,
  formatDraftPeriodStart,
  reportingYearFromInterval,
} from 'modules/FreeSupplier/supplierDraftPresentation';
import { formatSupplierPublicEmissions } from 'modules/FreeSupplier/supplierPublicPresentation';

// New-build surface (D48 — no Fable equivalent, V2 form only, never default
// HTML inputs): edits the PATCHABLE `proposedFactor` fields of a draft per
// updateSupplierFactorDraftRequestV1Schema — emissions.direct/indirect
// (total is derived, never independently patched — matches how the ingestion
// pipeline computes it) and authoritativeSefa.total/benchmark/benchmarkSource/
// production/precursor.
//
// reportingYear and cbamFactor are BOTH patched but neither is entered: the
// year derives from the draft's interval (its last record date) and the factor
// derives from the year via the phase-in table, so the chain matches the
// ingestion that produced the draft. They render read-only beside the period.
// `sourceSefaEvidence` is likewise read-only, labelled as source evidence.
//
// SEFA fields (product owner feedback): total/production/precursor come from
// the ingestion pipeline and render READ-ONLY by default — a supplier reviews
// them, they don't retype them. An explicit "Enter SEFA manually" toggle
// unlocks all three for the supplier who wants to input a final SEFA
// themselves (the production + precursor = total invariant still applies).
// A draft with an incomplete SEFA (e.g. a manually created S10 draft) starts
// in manual mode, since there is nothing computed to review. benchmark /
// benchmarkSource are NOT rendered at all anymore — they ride along as hidden
// pass-through state (same pattern as indirectEmissionFactorSource) so the
// replacement PATCH doesn't silently drop stored values.
//
// Scope note (flagged in the S7.4/S7.5 report): targetSeriesKey (CN code /
// product variant / production methods), interval, proposedCompany and
// proposedInstallation are ALSO technically patchable per the wire schema,
// but are NOT exposed here — they represent series identity/profile data
// already shown read-only in the DraftReviewPage header/provenance table, and
// re-identifying a series belongs with the catalog pickers (GoodsSelectField
// et al) rather than this first-cut numeric review form.
//
// Owns its own Save action (separate from the page's Discard / Confirm &
// publish footer) — saving values and confirming/publishing them are
// independent steps.
//
// Proposed-vs-currently-published (product owner feedback, replacing the old
// separate DraftProvenanceTable): a supplier shouldn't have to read a table
// of internal vocabulary (field/source value/proposed value/confidence/
// currently published) to understand what they're about to overwrite. Instead
// each field that has a currently-published counterpart AND differs from it
// gets a small inline hint ("Currently published: X — will be replaced when
// you confirm") right under that field's input, via FormField's `hint` slot.
// When the target series/period has nothing published yet at all, a single
// quiet note replaces every per-field hint instead of "Currently published: —"
// noise on every field.

// A7/item-12: bounds are DERIVED from the canonical draft contract
// (supplierDraftContracts.js supplierFactorDraftProposedFactorV1Schema — the
// JS mirror of backend/src/contracts/supplierFactorDraft.ts) rather than
// restated as local literals — each field's `.safeParse(Number(v))` delegates
// to the SAME nonnegative/max/int/min bound the wire schema enforces, so a
// bound change there can't silently drift out of sync with this form. Only
// "required" and "parses as a number" are genuinely local UI concerns the
// (number-typed) contract schema doesn't itself express for a string input.
const emissionsBounds = supplierFactorDraftProposedFactorV1Schema.shape.emissions.shape;
const sefaBounds = supplierFactorDraftProposedFactorV1Schema.shape.authoritativeSefa.shape;
const { reportingYear: reportingYearBounds } = supplierFactorDraftProposedFactorV1Schema.shape;

const boundIssueMessage = (boundSchema, numericValue, fallback) =>
  boundSchema.safeParse(numericValue).error?.issues?.[0]?.message || fallback;

const numberString = (label, boundSchema) =>
  z
    .string()
    .min(1, `${label} is required`)
    .refine((v) => Number.isFinite(Number(v)), `Enter a valid number for ${label.toLowerCase()}`)
    .refine(
      (v) => boundSchema.safeParse(Number(v)).success,
      (v) => ({ message: boundIssueMessage(boundSchema, Number(v), `${label} is out of range`) }),
    );

const optionalNumberString = (label, boundSchema) =>
  z
    .string()
    .optional()
    .refine((v) => !v || Number.isFinite(Number(v)), 'Enter a valid number')
    .refine(
      (v) => !v || boundSchema.safeParse(Number(v)).success,
      (v) => ({ message: boundIssueMessage(boundSchema, Number(v), `${label} is out of range`) }),
    );

// Item 5: mirrors the backend's Template-origin SEFA-split invariant exactly
// (supplierFactorPublication.ts normalizeSefa — ALL drafts confirm as
// Template origin): production and precursor SEFA must both be present and
// sum EXACTLY to total SEFA (Decimal.eq, no floating-point tolerance — hence
// decimal.js here too, not raw `+`, which can't represent e.g. 0.1 + 0.2
// exactly). Attached to both split fields so the guidance shows next to
// whichever one the reviewer is looking at, per Compose-with-item-12.
const SEFA_SPLIT_MESSAGE = 'Production + precursor SEFA must equal total SEFA';

// Exported for a direct, dependency-free bounds test (A7) — see
// ProposedFactorForm.test.js.
// The indirect/electricity breakdown fields (S16) are all OPTIONAL — a
// summary-only draft has none, and a supplier may know only some. When BOTH
// factor and consumed are supplied, `indirect` is derived from them (read-only);
// otherwise `indirect` stays directly editable (backward compatible). Bounds are
// nonnegative; share of default is a 0–100 percentage.
const nonNegNumber = z.number().finite().nonnegative();
const percentNumber = z.number().finite().nonnegative().max(100);

export const proposedFactorFormSchema = z
  .object({
    direct: numberString('Direct emissions', emissionsBounds.direct),
    indirect: numberString('Indirect emissions', emissionsBounds.indirect),
    sefaTotal: numberString('Total SEFA', sefaBounds.total),
    // No longer rendered — hidden pass-through so the replacement PATCH keeps
    // any stored benchmark/benchmarkSource instead of silently dropping them.
    benchmark: optionalNumberString('Benchmark', sefaBounds.benchmark),
    benchmarkSource: z.string().optional(),
    production: numberString('Production SEFA', sefaBounds.production),
    precursor: numberString('Precursor SEFA', sefaBounds.precursor),
    // Neither cbamFactor nor reportingYear is an INPUT. reportingYear is
    // derived from the draft's interval (its last record date) and carried as
    // hidden form state so the wire bounds still apply and the cbamFactor
    // derivation below has a single source; cbamFactor is then derived from it
    // via the canonical phase-in table (getCbamFactorForYear).
    reportingYear: numberString('Reporting year', reportingYearBounds),
    // Indirect/electricity breakdown (all optional).
    indirectEmissionFactor: optionalNumberString('Electricity emission factor', nonNegNumber),
    electricityConsumed: optionalNumberString('Electricity consumed', nonNegNumber),
    indirectEmissionFactorSourceValue: z.string().optional(),
    electricitySource: z.enum(['SOE01', 'SOE02', 'SOE03']).or(z.literal('')).optional(),
    otherSourceIndication: z.string().optional(),
    shareDefaultEmissions: optionalNumberString('Share of default emissions', percentNumber),
    // Contract-supported breakdown fields the form does NOT render yet, carried
    // as hidden state so the replacement PATCH round-trips them instead of
    // silently dropping them (which would make publication fall back to defaults).
    indirectEmissionFactorSource: z.enum(['01', '02']).or(z.literal('')).optional(),
    determinationType: z.enum(['01', '02', '03']).or(z.literal('')).optional(),
  })
  .superRefine((values, ctx) => {
    const production = Number(values.production);
    const precursor = Number(values.precursor);
    const total = Number(values.sefaTotal);
    if (!Number.isFinite(production) || !Number.isFinite(precursor) || !Number.isFinite(total)) {
      return; // already flagged by the field-level checks above
    }
    if (!new Decimal(production).plus(precursor).eq(total)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: SEFA_SPLIT_MESSAGE,
        path: ['production'],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: SEFA_SPLIT_MESSAGE,
        path: ['precursor'],
      });
    }
  });

/* --------------------------------------------------------------- currently-
   published hints (per field, replacing DraftProvenanceTable) ------------- */

// form field name → draft.fieldProvenance[].field path / SupplierFactorRevision
// path — the same dotted paths DraftProvenanceTable used to read, now
// resolved per-field instead of as table rows. `total` isn't in the wire
// PATCH (it's derived), but the backend still snapshots it in fieldProvenance
// and a published revision has it, so it gets a hint too.
const FIELD_PROVENANCE_PATHS = Object.freeze({
  direct: 'proposedFactor.emissions.direct',
  indirect: 'proposedFactor.emissions.indirect',
  total: 'proposedFactor.emissions.total',
  sefaTotal: 'proposedFactor.authoritativeSefa.total',
  benchmark: 'proposedFactor.authoritativeSefa.benchmark',
  benchmarkSource: 'proposedFactor.authoritativeSefa.benchmarkSource',
  production: 'proposedFactor.authoritativeSefa.production',
  precursor: 'proposedFactor.authoritativeSefa.precursor',
  reportingYear: 'proposedFactor.reportingYear',
});

// A field's own fieldProvenance row when the draft has one, else a synthetic
// row with no snapshot — currentlyPublishedDisplayValue still resolves the
// LIVE value off `matchedFactor` for a synthetic row; it only falls back to
// `undefined` (→ no hint) when there's neither a live match nor a captured
// snapshot. This is what makes a manually created draft (S10 — no source,
// so no fieldProvenance at all) degrade gracefully to "no hint" instead of
// crashing on a missing row.
const fieldProvenanceRowFor = (path, fieldProvenance) =>
  (fieldProvenance || []).find((row) => row.field === path) || {
    field: path,
    currentlyPublishedValue: undefined,
  };

// Reporting year is an identifier-like integer — must render ungrouped, e.g.
// "2026", not "2,026" (a prior FE bug) or "2026.0000". Every other field here
// is an emissions/SEFA/CBAM figure, formatted with the SAME formatter the
// public supplier page/widget already use (formatSupplierPublicEmissions) so
// this hint's numbers match what a viewer of the published page would see.
const formatPublishedFieldValue = (formField, value) => {
  if (formField === 'reportingYear') return String(value);
  if (typeof value === 'number') return formatSupplierPublicEmissions(value);
  return String(value);
};

// Whether the PROPOSED form value for a field actually differs from its
// currently-published counterpart. Blank/unparseable proposed values defer
// to the field's own zod validation rather than reporting a spurious diff;
// numeric comparison is Decimal-exact (mirrors the SEFA-split invariant
// above, not raw `!==`/`-`, which can't represent e.g. 0.1 + 0.2 exactly).
const proposedValueDiffers = (formField, rawFormValue, published) => {
  if (formField === 'benchmarkSource') {
    const proposed = (rawFormValue || '').trim();
    return proposed !== '' && proposed !== published;
  }
  if (rawFormValue === '' || rawFormValue == null) return false;
  const numeric = Number(rawFormValue);
  if (!Number.isFinite(numeric)) return false;
  return !new Decimal(numeric).eq(new Decimal(Number(published)));
};

// The per-field hint map for the form, plus whether the target series/period
// has anything published at all. `formValues` is the live form values object
// (RHF strings) with `total` merged in (it's derived, not an RHF field).
// Reuses findMatchingSupplierFactor (live series lookup) and
// currentlyPublishedDisplayValue (live-else-snapshot resolution) rather than
// reimplementing either.
export function resolveProposedFactorHints(formValues, draft, matchedFactor) {
  const publishedByField = {};
  for (const [formField, path] of Object.entries(FIELD_PROVENANCE_PATHS)) {
    const row = fieldProvenanceRowFor(path, draft?.fieldProvenance);
    publishedByField[formField] = currentlyPublishedDisplayValue(row, matchedFactor);
  }
  const hasPublishedFactor = Object.values(publishedByField).some((value) => value != null);
  const hints = {};
  if (hasPublishedFactor) {
    for (const [formField, published] of Object.entries(publishedByField)) {
      if (published == null) continue;
      if (!proposedValueDiffers(formField, formValues[formField], published)) continue;
      hints[formField] = formatPublishedFieldValue(formField, published);
    }
  }
  return { hasPublishedFactor, hints };
}

const asString = (value) => (value == null ? '' : String(value));

export function draftToProposedFactorFormValues(draft) {
  const emissions = draft?.proposedFactor?.emissions || {};
  const sefa = draft?.proposedFactor?.authoritativeSefa || {};
  const detail = draft?.proposedFactor?.indirectDetail || {};
  return {
    direct: asString(emissions.direct),
    indirect: asString(emissions.indirect),
    sefaTotal: asString(sefa.total),
    benchmark: asString(sefa.benchmark),
    benchmarkSource: sefa.benchmarkSource || '',
    production: asString(sefa.production),
    precursor: asString(sefa.precursor),
    // DERIVED from the draft's interval, not read off the stored proposal: the
    // interval is the draft's immutable scope, and the ingestion that produced
    // this draft derived the year from it the same way. The stored value is
    // only a fallback for a draft whose interval is missing/unparseable.
    reportingYear: asString(
      reportingYearFromInterval(draft?.interval) ?? draft?.proposedFactor?.reportingYear,
    ),
    // Indirect/electricity breakdown — seeded from the draft's `indirectDetail`
    // (present on template-upload drafts and any new-format factor); a
    // summary-only draft has none, so these stay blank and `indirect` remains
    // directly editable.
    indirectEmissionFactor: asString(detail.indirectEmissionFactor),
    electricityConsumed: asString(detail.electricityConsumed),
    indirectEmissionFactorSourceValue: detail.indirectEmissionFactorSourceValue || '',
    electricitySource: detail.electricitySource || '',
    otherSourceIndication: detail.otherSourceIndication || '',
    shareDefaultEmissions: asString(detail.shareDefaultEmissions),
    // Hidden pass-through fields (not rendered) — round-tripped so the
    // replacement PATCH doesn't drop them.
    indirectEmissionFactorSource: detail.indirectEmissionFactorSource || '',
    determinationType: detail.determinationType || '',
  };
}

// True when the draft seeded every SEFA value the wire schema requires — the
// form then opens with the SEFA fields read-only (review mode). An incomplete
// SEFA (manually created draft) opens in manual mode instead, since there is
// nothing computed to review. Exported for ProposedFactorForm.test.js.
export function sefaValuesComplete(values) {
  return [values.sefaTotal, values.production, values.precursor].every(
    (value) => value !== '' && value != null && Number.isFinite(Number(value)),
  );
}

// True when both electricity fields are supplied, so `indirect` is derived from
// them (factor × consumed) rather than entered directly.
export function hasElectricityBreakdown(values) {
  return Boolean(values.indirectEmissionFactor) && Boolean(values.electricityConsumed);
}

// The derived specific-indirect value (factor × consumed, 7dp) — the single
// source of the derive math, shared by the live form and the patch builder.
export function deriveIndirectFromBreakdown(values) {
  const factor = Number(values.indirectEmissionFactor);
  const consumed = Number(values.electricityConsumed);
  if (!Number.isFinite(factor) || !Number.isFinite(consumed)) return null;
  return new Decimal(factor).times(consumed).toDecimalPlaces(7).toNumber();
}

// form values → the `proposedFactor.indirectDetail` block, or `{}` when the
// supplier cleared/never set a breakdown. Always sent (S14 replacement
// semantics: `{}` clears any prior breakdown, a populated object replaces it).
function formValuesToIndirectDetail(values) {
  const detail = {};
  if (values.indirectEmissionFactor)
    detail.indirectEmissionFactor = Number(values.indirectEmissionFactor);
  if (values.electricityConsumed) detail.electricityConsumed = Number(values.electricityConsumed);
  if (values.electricitySource) detail.electricitySource = values.electricitySource;
  // Trim FIRST, then only keep non-empty — a whitespace-only string would
  // otherwise become "" and be rejected by the backend `.trim().min(1)`.
  const sourceValue = (values.indirectEmissionFactorSourceValue || '').trim();
  if (sourceValue) detail.indirectEmissionFactorSourceValue = sourceValue;
  const otherSource = (values.otherSourceIndication || '').trim();
  if (otherSource) detail.otherSourceIndication = otherSource;
  if (values.shareDefaultEmissions !== '' && values.shareDefaultEmissions != null) {
    detail.shareDefaultEmissions = Number(values.shareDefaultEmissions);
  }
  // Hidden contract-supported fields — carried through so the replacement PATCH
  // doesn't silently delete them (which would fall publication back to defaults).
  if (values.indirectEmissionFactorSource) {
    detail.indirectEmissionFactorSource = values.indirectEmissionFactorSource;
  }
  if (values.determinationType) detail.determinationType = values.determinationType;
  return detail;
}

// form values → PATCH /supplier/drafts/:id body's `proposedFactor` block.
// total is always re-derived from direct + indirect, matching how the
// ingestion pipeline computed it originally.
export function proposedFactorFormValuesToPatch(values) {
  const direct = Number(values.direct);
  // When a breakdown is supplied, indirect is the derived value (factor ×
  // consumed) — the same value the read-only field shows; otherwise it's the
  // directly-entered indirect. Keeps the persisted summary consistent with the
  // breakdown the backend also re-checks at publish time.
  const indirect = hasElectricityBreakdown(values)
    ? (deriveIndirectFromBreakdown(values) ?? Number(values.indirect))
    : Number(values.indirect);
  // production/precursor are now REQUIRED (item 5 — the backend rejects a
  // Template-origin factor missing either), always included.
  const authoritativeSefa = {
    total: Number(values.sefaTotal),
    production: Number(values.production),
    precursor: Number(values.precursor),
  };
  if (values.benchmark) authoritativeSefa.benchmark = Number(values.benchmark);
  if (values.benchmarkSource) authoritativeSefa.benchmarkSource = values.benchmarkSource;
  return {
    proposedFactor: {
      emissions: {
        direct,
        indirect,
        // Decimal-exact sum — the backend validates total === direct + indirect
        // exactly. Raw float addition (0.1 + 0.2 = 0.30000000000000004) or a
        // fixed 6dp rounding both drift from that check the moment a 7dp value
        // (which the wire schema allows) is involved.
        total: new Decimal(direct).plus(indirect).toNumber(),
      },
      authoritativeSefa,
      indirectDetail: formValuesToIndirectDetail(values),
      // Derived, not entered: the CBAM factor is a fixed function of the
      // reporting year (the phase-in table — 97.5% in 2026 → 0% in 2034).
      cbamFactor: getCbamFactorForYear(values.reportingYear),
      reportingYear: Number(values.reportingYear),
    },
  };
}

function SourceEvidence({ evidence }) {
  const { t } = useTranslation('freeSupplier');
  if (!evidence) return null;
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3 mb-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 m-0">
        {t(
          'proposedFactorForm.sourceEvidenceHeading',
          'Source evidence — from the template, not the corrected split',
        )}
      </p>
      <div className="mt-1.5 grid grid-cols-3 gap-3 text-xs text-slate-600">
        <span>
          {t('proposedFactorForm.productionLabel', 'Production')}{' '}
          <span className="font-semibold text-slate-800">{evidence.production ?? '—'}</span>
        </span>
        <span>
          {t('proposedFactorForm.precursorLabel', 'Precursor')}{' '}
          <span className="font-semibold text-slate-800">{evidence.precursor ?? '—'}</span>
        </span>
        <span>
          {t('proposedFactorForm.totalLabel', 'Total')}{' '}
          <span className="font-semibold text-slate-800">{evidence.total ?? '—'}</span>
        </span>
      </div>
    </div>
  );
}

// Compact inline hint under a field whose proposed value would overwrite a
// currently-published one — muted text with a small amber accent (this app's
// existing convention for "this changed", e.g. the draft state chips and the
// 409-conflict banner on DraftReviewPage), rather than a whole extra column.
// Wraps normally (no whitespace-nowrap) so it stays clean on a 390px screen.
function PublishedValueHint({ value }) {
  const { t } = useTranslation('freeSupplier');
  if (!value) return null;
  return (
    <p className="flex items-start gap-1.5 text-[11px] leading-snug text-amber-700 m-0">
      <span
        className="mt-1 inline-block w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"
        aria-hidden="true"
      />
      <span>
        {t('proposedFactorForm.publishedHintPrefix', 'Currently published:')}{' '}
        <span className="font-semibold">{value}</span>{' '}
        {t('proposedFactorForm.publishedHintSuffix', '— will be replaced when you confirm')}
      </span>
    </p>
  );
}

// Span classes are LOOKED UP, never interpolated: this app ships a PREBUILT
// Tailwind bundle, so a class assembled at runtime (`md:col-span-${n}`) is
// never emitted and the element silently falls back to full width.
const DERIVED_SPAN_CLASS = Object.freeze({
  4: 'col-span-12 md:col-span-4',
  6: 'col-span-12 md:col-span-6',
});

// A value the supplier cannot type — derived from the draft (its interval) or
// from other fields (total, CBAM factor). Matches the height/rhythm of the V2
// FormField beside it so a mixed row still lines up.
function DerivedValue({ label, value, note, children, colSpan = 6 }) {
  return (
    <div className={DERIVED_SPAN_CLASS[colSpan] || DERIVED_SPAN_CLASS[6]}>
      <p className="text-xs font-medium text-slate-500 mb-1 mt-0">{label}</p>
      <p className="text-sm font-bold text-primaryColor m-0 py-2">{value}</p>
      {note ? <p className="text-[11px] leading-snug text-slate-400 m-0">{note}</p> : null}
      {children}
    </div>
  );
}

// Shown once, at the top of the form, INSTEAD of any per-field hints, when
// this target series/period has nothing published yet — a quiet note rather
// than "Currently published: —" repeated on every field.
function NoPublishedFactorNotice() {
  const { t } = useTranslation('freeSupplier');
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3 mb-4 text-xs text-slate-500">
      {t(
        'proposedFactorForm.noPublishedFactorNotice',
        'Nothing is published yet for this product — confirming will publish it for the first time.',
      )}
    </div>
  );
}

export default function ProposedFactorForm({ draft, onSave, saving = false, factorItems }) {
  const { t } = useTranslation('freeSupplier');
  const seededValues = useMemo(() => draftToProposedFactorFormValues(draft), [draft]);
  const methods = useCgForm({
    schema: proposedFactorFormSchema,
    defaultValues: seededValues,
  });
  const values = methods.watch();
  const { setValue } = methods;

  // SEFA review-vs-manual mode. A draft whose pipeline-computed SEFA is
  // complete opens read-only; the supplier opts into editing explicitly.
  // Reverting restores the draft's own values, so "manual" can always be
  // walked back without losing the computed split.
  const seededSefaComplete = sefaValuesComplete(seededValues);
  const [manualSefa, setManualSefa] = useState(!seededSefaComplete);
  const revertToComputedSefa = () => {
    setValue('sefaTotal', seededValues.sefaTotal, { shouldValidate: true });
    setValue('production', seededValues.production, { shouldValidate: true });
    setValue('precursor', seededValues.precursor, { shouldValidate: true });
    setManualSefa(false);
  };

  // When both electricity fields are supplied, `indirect` is DERIVED (factor ×
  // consumed) and shown read-only — the supplier edits the breakdown, not the
  // summary. With no breakdown, `indirect` stays directly editable.
  const factorStr = values.indirectEmissionFactor;
  const consumedStr = values.electricityConsumed;
  const breakdownPresent = Boolean(factorStr) && Boolean(consumedStr);
  useEffect(() => {
    if (!breakdownPresent) return;
    const derived = deriveIndirectFromBreakdown({
      indirectEmissionFactor: factorStr,
      electricityConsumed: consumedStr,
    });
    if (derived != null) setValue('indirect', String(derived), { shouldValidate: true });
  }, [breakdownPresent, factorStr, consumedStr, setValue]);

  const direct = Number(values.direct);
  const indirect = Number(values.indirect);
  // Decimal-exact, matching the patch builder — raw `+` shows float artifacts
  // (1.1 + 2.2 → 3.3000000000000003) and diverges from what gets published.
  const total =
    Number.isFinite(direct) && Number.isFinite(indirect)
      ? new Decimal(direct).plus(indirect).toNumber()
      : null;
  // Reporting year is hidden form state seeded from the draft's interval, so
  // the derived CBAM factor and the read-only display below both read it from
  // the single value the patch will actually send.
  const reportingYear = values.reportingYear;
  const derivedCbamFactor = reportingYear ? getCbamFactorForYear(reportingYear) : null;

  const matchedFactor = useMemo(
    () => findMatchingSupplierFactor(draft, factorItems),
    [draft, factorItems],
  );
  const { hasPublishedFactor, hints } = resolveProposedFactorHints(
    { ...values, total },
    draft,
    matchedFactor,
  );

  const handleSubmit = async (formValues) => {
    await onSave?.(proposedFactorFormValuesToPatch(formValues));
  };

  return (
    <Form
      methods={methods}
      onSubmit={handleSubmit}
    >
      {!hasPublishedFactor ? <NoPublishedFactorNotice /> : null}

      {/* The period this draft covers, repeated from the page header so the
          values below are never read without the dates that scope them. It
          comes from the draft's immutable interval — nothing here is editable
          (re-scoping a draft belongs with the series identity, not this form).
          The CBAM factor sits beside the year it derives from. */}
      <FormSection title={t('proposedFactorForm.reportingPeriodTitle', 'Reporting period')}>
        <DerivedValue
          label={t('proposedFactorForm.periodLabel', 'Period')}
          value={`${formatDraftPeriodStart(draft?.interval)} – ${formatDraftLastRecordDate(
            draft?.interval,
          )}`}
          colSpan={4}
        />
        <DerivedValue
          label={t('proposedFactorForm.reportingYearLabel', 'Reporting year (derived)')}
          value={reportingYear || '—'}
          note={t(
            'proposedFactorForm.reportingYearNote',
            "Taken from the period's last record date",
          )}
          colSpan={4}
        >
          <PublishedValueHint value={hints.reportingYear} />
        </DerivedValue>
        <DerivedValue
          label={t('proposedFactorForm.cbamFactorLabel', 'CBAM factor (derived)')}
          value={derivedCbamFactor == null ? '—' : `${derivedCbamFactor}%`}
          note={t('proposedFactorForm.cbamFactorNote', 'Set automatically from the reporting year')}
          colSpan={4}
        />
      </FormSection>

      <FormSection title={t('proposedFactorForm.proposedFactorTitle', 'Proposed factor')}>
        <FormField
          name="direct"
          label={t('proposedFactorForm.directEmissionsLabel', 'Direct emissions (tCO2e/t)')}
          required
          colSpan={4}
          hint={<PublishedValueHint value={hints.direct} />}
        >
          <TextField numeric />
        </FormField>
        <FormField
          name="indirect"
          label={t('proposedFactorForm.indirectEmissionsLabel', 'Indirect emissions (tCO2e/t)')}
          required
          colSpan={4}
          hint={
            breakdownPresent ? (
              <p className="text-[11px] leading-snug text-slate-400 m-0">
                {t(
                  'proposedFactorForm.derivedFromBreakdownHint',
                  'Derived from electricity factor × consumed below',
                )}
              </p>
            ) : (
              <PublishedValueHint value={hints.indirect} />
            )
          }
        >
          <TextField
            numeric
            disabled={breakdownPresent}
          />
        </FormField>
        <DerivedValue
          label={t('proposedFactorForm.totalDerivedLabel', 'Total (derived)')}
          value={total == null || Number.isNaN(total) ? '—' : String(total)}
          colSpan={4}
        >
          <PublishedValueHint value={hints.total} />
        </DerivedValue>
      </FormSection>

      <IndirectEmissionsFields />

      {/* SEFA is computed by the ingestion pipeline and reviewed, not retyped —
          fields unlock only behind the explicit manual toggle. Benchmark /
          benchmark source are no longer shown (hidden pass-through state). */}
      <FormSection title={t('proposedFactorForm.sefaSectionTitle', 'SEFA')}>
        {manualSefa ? (
          <>
            <FormField
              name="production"
              label={t('proposedFactorForm.productionSefaLabel', 'Production SEFA')}
              required
              colSpan={4}
              hint={<PublishedValueHint value={hints.production} />}
            >
              <TextField numeric />
            </FormField>
            <FormField
              name="precursor"
              label={t('proposedFactorForm.precursorSefaLabel', 'Precursor SEFA')}
              required
              colSpan={4}
              hint={<PublishedValueHint value={hints.precursor} />}
            >
              <TextField numeric />
            </FormField>
            <FormField
              name="sefaTotal"
              label={t('proposedFactorForm.totalSefaLabel', 'Total SEFA')}
              required
              colSpan={4}
              hint={<PublishedValueHint value={hints.sefaTotal} />}
            >
              <TextField numeric />
            </FormField>
          </>
        ) : (
          <>
            <DerivedValue
              label={t('proposedFactorForm.productionSefaLabel', 'Production SEFA')}
              value={values.production === '' ? '—' : values.production}
              colSpan={4}
            >
              <PublishedValueHint value={hints.production} />
            </DerivedValue>
            <DerivedValue
              label={t('proposedFactorForm.precursorSefaLabel', 'Precursor SEFA')}
              value={values.precursor === '' ? '—' : values.precursor}
              colSpan={4}
            >
              <PublishedValueHint value={hints.precursor} />
            </DerivedValue>
            <DerivedValue
              label={t('proposedFactorForm.totalSefaLabel', 'Total SEFA')}
              value={values.sefaTotal === '' ? '—' : values.sefaTotal}
              colSpan={4}
            >
              <PublishedValueHint value={hints.sefaTotal} />
            </DerivedValue>
          </>
        )}
        <div className="col-span-12">
          {manualSefa ? (
            seededSefaComplete ? (
              <button
                type="button"
                className="text-xs font-semibold text-primaryColor hover:underline bg-transparent border-0 p-0"
                onClick={revertToComputedSefa}
              >
                {t(
                  'proposedFactorForm.useComputedSefaButton',
                  'Use the computed SEFA values instead',
                )}
              </button>
            ) : null
          ) : (
            <button
              type="button"
              className="text-xs font-semibold text-primaryColor hover:underline bg-transparent border-0 p-0"
              onClick={() => setManualSefa(true)}
            >
              {t('proposedFactorForm.enterSefaManuallyButton', 'Enter SEFA manually')}
            </button>
          )}
        </div>
      </FormSection>

      <SourceEvidence evidence={draft?.proposedFactor?.sourceSefaEvidence} />

      <FormActions
        submitLabel={t('proposedFactorForm.saveChangesButton', 'Save changes')}
        loading={saving}
      />
    </Form>
  );
}
